import { z } from 'zod';
import { CityId } from './cities';
import { SerialTeaser } from './manga';
import {
  ActionType,
  CheckTier,
  EpisodeState,
  EpistemicStatus,
  EventKind,
  Expression,
  LocationId,
  NpcId,
  RelationshipStage,
  RenderTier,
  SkillId,
  Slot,
  Stat,
  Weather,
} from './enums';

// ---------- world ----------

export const LocationView = z.object({
  id: LocationId,
  name: z.string(),
  description: z.string(),
  imageUrl: z.string(),
  unlocked: z.boolean(),
  storyCount: z.number().int(),
  activityCount: z.number().int(),
  presentNpcIds: z.array(NpcId),
  /** Neighbours here you haven't met yet: counted, never named. */
  strangerCount: z.number().int(),
  /** What's here right now: your job, and lessons you can take. */
  work: z.boolean().optional(),
  lessonCount: z.number().int().optional(),
});
export type LocationView = z.infer<typeof LocationView>;

export const ActivityView = z.object({
  id: z.string(),
  locationId: LocationId,
  name: z.string(),
  description: z.string(),
  slotCost: z.number().int(),
  stats: z.array(Stat),
  skills: z.array(SkillId),
  lifeSkill: z.string().nullable(),
  lesson: z.enum(['drill', 'timing', 'none']),
  imageUrl: z.string(),
  /** What one practice gives right now (today's repeat yield, before the lesson grade). */
  gains: z.array(z.object({ kind: z.enum(['stat', 'skill', 'life']), id: z.string(), amount: z.number().int() })),
  yieldPercent: z.number().int(),
  available: z.boolean(),
  lockedReason: z.string().nullable(),
});
export type ActivityView = z.infer<typeof ActivityView>;

export const StoryCard = z.object({
  templateKey: z.string(),
  kind: EventKind,
  title: z.string(),
  synopsis: z.string(),
  locationId: LocationId,
  castNpcIds: z.array(NpcId),
  slotCost: z.number().int(),
  coverUrl: z.string(),
  badges: z.array(z.enum(['new', 'follow_up', 'rare', 'romance', 'friendship', 'premium'])),
  available: z.boolean(),
  lockedHint: z.string().nullable(),
  inProgressEpisodeId: z.string().nullable(),
  /** Who you'd meet — set only on opening (first-meeting) cards, so choosing isn't a blind guess. */
  lead: z.object({ npcId: NpcId, name: z.string(), portraitUrl: z.string() }).nullable().optional(),
  /** 個人連載 not yet written: 'unwritten' until the player picks it, then 'writing'. Absent once it can be played. */
  writeState: z.enum(['unwritten', 'writing']).nullable().optional(),
});
export type StoryCard = z.infer<typeof StoryCard>;

/** Template keys of chapters written for one player (個人連載): E90_p<scope>_<n>. */
export const isPersonalKey = (key: string) => key.startsWith('E90_p');

/** POST /stories/:templateKey/write — a personal chapter is written when the player picks it; poll until ready. */
export const WriteChapterResponse = z.object({ state: z.enum(['writing', 'ready', 'failed']) });
export type WriteChapterResponse = z.infer<typeof WriteChapterResponse>;

/** 看板娘: the character who greets the player on Home, where they are right now. */
export const HomeCompanion = z.object({
  npcId: NpcId,
  name: z.string(),
  portraitUrl: z.string(),
  /** Full-body art (transparent) for page heroes. */
  figureUrl: z.string(),
  locationId: LocationId,
  greeting: z.string(),
  stage: RelationshipStage,
  stageLabel: z.string(),
  /** Affection shown as 0–5 hearts. */
  hearts: z.number().int().min(0).max(5),
  /** True when the player picked this character; false when the game chose the closest one. */
  chosen: z.boolean(),
});
export type HomeCompanion = z.infer<typeof HomeCompanion>;

// ---------- energy (精力) ----------

/** The pace of play in real time. Paying refills energy; it never buys success (plan §19). */
export const ENERGY = {
  /** The tank by tier is MEMBERSHIP.maxEnergy; subscribers (Plus, Premium) regenerate faster. */
  regenMinutes: 20,
  plusRegenMinutes: 15,
  cost: { training: 2, story: 3, whatIf: 2, work: 2 },
  /**
   * Ink coins for the 1st, 2nd, 3rd … refill of the day: light players refill cheaply, keen ones pay
   * more, and there is a daily cap so nobody spends without limit.
   */
  refillCoins: [100, 150, 200, 200, 200],
} as const;
/** Refills allowed per day (the length of the price ladder). */
export const MAX_REFILLS_PER_DAY = ENERGY.refillCoins.length;

export const EnergyView = z.object({
  value: z.number().int(),
  max: z.number().int(),
  /** When the next point arrives / energy is full (ISO); null when already full. */
  nextAt: z.string().nullable(),
  fullAt: z.string().nullable(),
  /** Ink coins the next refill costs today; null once today's refills are used up. */
  refillCoins: z.number().int().nullable(),
  refillsLeftToday: z.number().int(),
});
export type EnergyView = z.infer<typeof EnergyView>;

/** 今日行程: what the player can do today, in order, each one tap away. */
export const AgendaItem = z.object({
  id: z.string(),
  kind: z.enum(['continue', 'chapter', 'work', 'practice', 'pet']),
  title: z.string(),
  subtitle: z.string(),
  done: z.boolean(),
  action: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('story'), templateKey: z.string(), inProgressEpisodeId: z.string().nullable() }),
    z.object({ kind: z.literal('location'), locationId: LocationId }),
    z.object({ kind: z.literal('lesson'), activityId: z.string() }),
    z.object({ kind: z.literal('pet') }),
  ]),
});
export type AgendaItem = z.infer<typeof AgendaItem>;

/**
 * Today's work across all jobs: `used` shifts so far (paid ones included), `max` free ones, and
 * how many more ink coins can still buy (`extraLeft`, `extraCoins` each).
 */
export const WorkDay = z.object({ used: z.number().int(), max: z.number().int(), extraLeft: z.number().int(), extraCoins: z.number().int() });
export type WorkDay = z.infer<typeof WorkDay>;
/** Past the day's free shifts, ink coins buy one more, a few times a day (energy still applies). */
export const EXTRA_SHIFTS = { perDay: 2, coins: 50 } as const;
/**
 * Working a shift without playing it (直接上班) is going through the motions: 70% of the pay, half
 * the career XP, and your coworker doesn't get to know you. Even a bad game beats it, so skipping
 * is a convenience, never the better deal.
 */
export const SKIPPED_SHIFT = { pay: 0.7, xp: 0.5 } as const;
/** One reading of a work day for every screen and the server: free shifts left, and whether an extra can be bought. */
export function workDayState(d: WorkDay) {
  const extrasUsed = EXTRA_SHIFTS.perDay - d.extraLeft;
  const freeUsed = d.used - extrasUsed;
  const freeFull = freeUsed >= d.max;
  return { extrasUsed, freeUsed, freeLeft: Math.max(0, d.max - freeUsed), freeFull, canExtra: freeFull && d.extraLeft > 0, allDone: freeFull && d.extraLeft === 0 };
}

export const TodayView = z.object({
  /** Real local time in the player's city; weather is that city's real weather. */
  clock: z.object({ gameDay: z.number().int(), slot: Slot, weather: Weather, city: CityId, timeZone: z.string(), utcOffsetMin: z.number().int(), tempC: z.number().nullable() }),
  continueEpisode: StoryCard.nullable(),
  featured: z.array(StoryCard),
  recentChapters: z.array(z.object({ chapterId: z.string(), title: z.string(), coverUrl: z.string().nullable(), phase: z.string() })),
  /** A birthday invite carries whose birthday it is (their person page is the door). */
  invites: z.array(z.object({ id: z.string(), kind: z.enum(['npc', 'room', 'friend', 'birthday']), title: z.string(), body: z.string(), npcId: NpcId.optional() })),
  goals: z.array(z.object({ id: z.string(), title: z.string(), progress: z.number().int(), target: z.number().int(), rewardCoins: z.number().int(), claimed: z.boolean() })),
  /** This week's story chapters towards WEEKLY_STORY_REWARD; `earned` once its coins are in. */
  weeklyStories: z.object({ progress: z.number().int(), target: z.number().int(), coins: z.number().int(), earned: z.boolean() }).optional(),
  /** Membership coins this visit delivered (days the server hadn't delivered yet; usually 0 — they arrive without a visit). */
  memberCoins: z.number().int().optional(),
  /** Welcome credits this visit delivered (WELCOME_CREDITS_MODE=day3: the first visit from the third game day); absent otherwise. */
  welcomeCredits: z.number().int().optional(),
  /** The serial's next chapter: 第 N 話 and the story it continues into (null before the first chapter or when no thread is open). */
  nextInSerial: z.object({ serialNo: z.number().int(), teaser: SerialTeaser }).nullable(),
  companion: HomeCompanion.nullable(),
  energy: EnergyView,
  /** 個人連載: new chapters for this player are being written right now. */
  storyWriting: z.boolean().optional(),
  agenda: z.array(AgendaItem).optional(),
  /** 昨日回顧: what happened on the previous game day (null when nothing did). */
  recap: z
    .object({
      day: z.number().int(),
      stories: z.array(z.string()),
      closer: z.array(z.object({ npcId: z.string(), name: z.string(), portraitUrl: z.string() })),
      met: z.array(z.string()),
      coinsEarned: z.number().int(),
      growth: z.array(z.string()),
      shifts: z.number().int(),
      gifts: z.number().int(),
    })
    .nullable()
    .optional(),
  /**
   * 今日班次: one per open job — in 社區 and at the workplace. `name` is the rank, `jobName` the
   * job; `work` says how many of the day's shifts are left (workDayState).
   */
  work: WorkDay.optional(),
  shifts: z
    .array(z.object({ careerId: z.string(), name: z.string(), jobName: z.string(), locationId: LocationId, coworkerName: z.string(), coworkerPortraitUrl: z.string(), workedToday: z.boolean(), shiftsUntilScene: z.number().int(), level: z.number().int() }))
    .optional(),
});
export type TodayView = z.infer<typeof TodayView>;

// ---------- skill lessons (技能課) ----------

/** What paying buys in lessons: time, never a better result than playing well (plan §19). */
export const LESSON_COSTS = {
  /** 速成: finish a lesson at grade A without playing, for ink coins. */
  skipCoins: 80,
  /** Subscribers (Plus, Premium) skip this many lessons a day for free. */
  plusFreeSkipsPerDay: 3,
} as const;

export const LessonGrade = z.enum(['S', 'A', 'B', 'C']);
export type LessonGrade = z.infer<typeof LessonGrade>;
export const TimingSkin = z.enum(['photography', 'music', 'cooking']);
export type TimingSkin = z.infer<typeof TimingSkin>;

const LessonSkip = z.object({ coins: z.number().int(), freeLeft: z.number().int() });
export const LessonView = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('drill'),
    lessonId: z.string(),
    skip: LessonSkip,
    drills: z.array(
      z.object({
        drillId: z.string(),
        speakerId: NpcId,
        speakerName: z.string(),
        portraitUrl: z.string(),
        context: z.string(),
        line: z.string(),
        options: z.array(z.object({ id: z.enum(['a', 'b', 'c']), text: z.string() })),
      }),
    ),
  }),
  z.object({
    kind: z.literal('timing'),
    lessonId: z.string(),
    skip: LessonSkip,
    skin: TimingSkin,
    /** The activity's place, drawn behind the mini game. */
    backgroundUrl: z.string(),
    /** The neighbour who plays along (the subject of the photo, the band mate, the cook). */
    helper: z.object({ npcId: NpcId, name: z.string(), portraitUrl: z.string() }),
    /** Game art by name (the cooking game's stove, wok stages, flame and ingredients). */
    sprites: z.record(z.string(), z.string()),
  }),
  z.object({ kind: z.literal('none') }),
]);
export type LessonView = z.infer<typeof LessonView>;

export const LessonAnswerRequest = z.object({ drillId: z.string().max(60), optionId: z.enum(['a', 'b', 'c']) });
export const LessonFeedback = z.object({ quality: z.enum(['best', 'ok', 'poor']), why: z.string(), bestOptionId: z.enum(['a', 'b', 'c']), bestWhy: z.string() });
export type LessonFeedback = z.infer<typeof LessonFeedback>;

export const TrainingRequest = z.object({
  activityId: z.string().max(60),
  /** The lesson just played (its answers are graded on the server). */
  lessonId: z.string().max(64).optional(),
  /** Timing-game score 0–100, reported by the client and clamped by the server. */
  timingScore: z.number().min(0).max(100).optional(),
  /** 速成: skip the lesson (free for subscribers up to a daily limit, otherwise ink coins). */
  skip: z.boolean().optional(),
});
export const GrowthDelta = z.object({
  xp: z.array(z.object({ stat: Stat, amount: z.number().int(), levelUps: z.number().int() })),
  skillXp: z.array(z.object({ skill: SkillId, amount: z.number().int(), levelUps: z.number().int() })),
  lifeSkillXp: z.array(z.object({ skill: z.string(), amount: z.number().int() })),
  coins: z.number().int(),
  items: z.array(z.object({ itemId: z.string(), name: z.string() })),
});
export type GrowthDelta = z.infer<typeof GrowthDelta>;

export const TrainingResult = z.object({
  narration: z.string(),
  /** Lesson grade (null for activities without a lesson). */
  grade: LessonGrade.nullable(),
  skipped: z.boolean(),
  growth: GrowthDelta,
  encounter: z.object({ templateKey: z.string(), title: z.string(), teaser: z.string() }).nullable(),
});
export type TrainingResult = z.infer<typeof TrainingResult>;

// ---------- episode reader ----------

export const StoryLine = z.object({
  kind: z.enum(['narration', 'dialogue', 'player', 'system']),
  speakerId: z.string().nullable(),
  speakerName: z.string().nullable(),
  text: z.string(),
});
export type StoryLine = z.infer<typeof StoryLine>;

export const ChoiceView = z.object({
  id: z.string(),
  label: z.string(),
  actionType: ActionType,
  available: z.boolean(),
  lockedReason: z.string().nullable(),
  /** Which growth the choice leans on, so a build feels useful without exposing numbers. */
  skillHint: z.object({ skill: SkillId.nullable(), stat: Stat.nullable(), confidence: z.enum(['comfortable', 'uncertain', 'stretch']) }).nullable(),
  /** 好友助攻: friends of the player's gender who make this romance check easier. */
  wingman: z.object({ amount: z.number().int(), friends: z.array(z.string()) }).nullable(),
});
export type ChoiceView = z.infer<typeof ChoiceView>;

export const TurnView = z.object({
  seq: z.number().int(),
  beatId: z.string(),
  backgroundUrl: z.string(),
  portraits: z.array(z.object({ npcId: NpcId, expression: Expression, url: z.string() })),
  lines: z.array(StoryLine),
  choices: z.array(ChoiceView),
  allowFreeText: z.boolean(),
  freeTextHint: z.string().nullable(),
  isFinal: z.boolean(),
});
export type TurnView = z.infer<typeof TurnView>;

export const CompletionSummary = z.object({
  outcomeId: z.string(),
  title: z.string(),
  summary: z.string(),
  growth: GrowthDelta,
  /** A romantic spark this story (romantic interest grew, or a dating milestone): the 心動 stamp. */
  spark: z.boolean(),
  relationshipChanges: z.array(z.object({ entityId: z.string(), name: z.string(), portraitUrl: z.string().nullable(), label: z.string(), /** 0–5 hearts after this story. */ hearts: z.number().int().min(0).max(5) })),
  learned: z.array(z.object({ factId: z.string(), text: z.string(), status: EpistemicStatus })),
  followUps: z.array(z.object({ templateKey: z.string(), title: z.string() })),
  manga: z.object({
    chapterId: z.string().nullable(),
    autoStarted: z.boolean(),
    jobId: z.string().nullable(),
    /** How it can be drawn: 'basic' free, 'illustrated' a full chapter of 8 pages for `credits` chapter tickets (0: the first one, free). */
    offers: z.array(z.object({ tier: RenderTier, credits: z.number().int(), available: z.boolean() })),
  }),
  /** Coins this chapter earned by completing the week's stories (WEEKLY_STORY_REWARD); only on the finishing turn. */
  weeklyCoins: z.number().int().optional(),
});
export type CompletionSummary = z.infer<typeof CompletionSummary>;

export const EpisodeView = z.object({
  id: z.string(),
  templateKey: z.string(),
  title: z.string(),
  state: EpisodeState,
  revision: z.number().int(),
  isCanonical: z.boolean(),
  current: TurnView,
  completion: CompletionSummary.nullable(),
});
export type EpisodeView = z.infer<typeof EpisodeView>;

export const StartEpisodeRequest = z.object({ templateKey: z.string().max(40) });

export const ActionPayload = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('choice'),
    choiceId: z.string().max(60),
    /** Player's own words attached to a preset action; preserved verbatim in canon. */
    speech: z.string().trim().max(200).optional(),
  }),
  z.object({ kind: z.literal('free_text'), text: z.string().trim().min(1).max(200) }),
]);
export type ActionPayload = z.infer<typeof ActionPayload>;

export const SubmitActionRequest = z.object({
  turnSeq: z.number().int().min(0),
  expectedRevision: z.number().int().min(0),
  action: ActionPayload,
});
export type SubmitActionRequest = z.infer<typeof SubmitActionRequest>;

export const ActionResponse = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('committed'),
    resolution: z.object({ choiceId: z.string(), tier: CheckTier, interpretedAs: z.string().nullable() }),
    episode: EpisodeView,
  }),
  /** Free text that would cause a persistent effect is confirmed before commit (plan §11.4). */
  z.object({
    status: z.literal('needs_confirmation'),
    interpretedChoiceId: z.string(),
    interpretedLabel: z.string(),
    speech: z.string(),
    alternatives: z.array(z.object({ choiceId: z.string(), label: z.string() })),
  }),
]);
export type ActionResponse = z.infer<typeof ActionResponse>;

/** GET /episodes/:id/turns — committed turns for the backlog, oldest first. */
export const BacklogTurn = z.object({
  id: z.string(),
  seq: z.number().int(),
  beatId: z.string(),
  lines: z.array(StoryLine),
  decision: z
    .object({ choiceId: z.string(), label: z.string(), speech: z.string().nullable(), tier: CheckTier, viaFreeText: z.boolean(), reactionLines: z.array(StoryLine) })
    .nullable(),
});
export type BacklogTurn = z.infer<typeof BacklogTurn>;

/** `coins` is the new balance after the reward. */
export const ClaimResult = z.object({ coins: z.number().int() });
export type ClaimResult = z.infer<typeof ClaimResult>;
export type StartEpisodeRequest = z.infer<typeof StartEpisodeRequest>;
export type TrainingRequest = z.infer<typeof TrainingRequest>;
